import React, { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { Input } from "antd";
import FormItem from "@/common/FormItem/FormItem";

export const PURGE_CONFIRM_TOKEN = "PURGE";

export type SongHistoryTombstonePurgeModalProps = {
  open: boolean;
  requestedCount: number;
  selectionMode: "ids" | "allMatching";
  busy: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

/**
 * Deleted-tab Tombstone Purge confirmation.
 * Reason is required for the API / tenancy audit — never sent to CloudWatch by design.
 */
const SongHistoryTombstonePurgeModal: React.FC<
  SongHistoryTombstonePurgeModalProps
> = ({
  open,
  requestedCount,
  selectionMode,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}) => {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");

  const handleExited = () => {
    setTyped("");
    setReason("");
  };

  const countLabel = requestedCount.toLocaleString();
  const canSubmit =
    typed === PURGE_CONFIRM_TOKEN && reason.trim().length > 0 && !busy;

  return (
    <Modal
      show={open}
      onHide={() => {
        if (!busy) onCancel();
      }}
      onExited={handleExited}
      centered
      backdrop={busy ? "static" : true}
      keyboard={!busy}
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Tombstone Purge</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger" className="mb-3">
          This permanently erases {countLabel} Deleted Song History tombstone
          {requestedCount === 1 ? "" : "s"}
          {selectionMode === "allMatching" ? " matching the current filters" : ""}
          . Song History META, frozen Detail, Track tombstone, and related
          projections are hard-deleted. This cannot be undone. There is no
          restore.
        </Alert>
        <p className="mb-3" style={{ color: "#444" }}>
          Requested count: <strong>{countLabel}</strong>
        </p>
        {errorMessage ? (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        ) : null}
        <FormItem label={`Type ${PURGE_CONFIRM_TOKEN} to confirm`} layout="vertical">
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoComplete="off"
            aria-label={`Type ${PURGE_CONFIRM_TOKEN} to confirm`}
          />
        </FormItem>
        <FormItem label="Purge reason" layout="vertical">
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            placeholder="Why are these tombstones being purged?"
          />
        </FormItem>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={!canSubmit}
          onClick={() => onConfirm(reason.trim())}
        >
          {busy
            ? "Purging…"
            : `Purge ${countLabel} Tombstone${requestedCount === 1 ? "" : "s"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SongHistoryTombstonePurgeModal;
